import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import { authService } from '@/services/authService';
import { setCredentials } from '@/redux/slices/authSlice';

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    defaultValues: import.meta.env.DEV
      ? { email: 'admin@edc.com', password: 'Admin@123' }
      : { email: '', password: '' },
  });
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (data) => {
    try {
      const res = await authService.login(data);
      dispatch(setCredentials({ token: res.token, user: res.user }));
      toast.success(`Welcome, ${res.user.name}!`);
      const dest = location.state?.from?.pathname || (res.user.role === 'admin' ? '/admin' : '/dashboard');
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    }
  };

  return (
    <div className="card p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold">Sign in to your account</h2>
        <p className="text-sm text-slate-500 mt-1">Welcome back. Please enter your details.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="email"
              autoComplete="email"
              className="input pl-9"
              placeholder="you@company.com"
              {...register('email', { required: 'Email is required' })}
            />
          </div>
          {errors.email && <p className="text-xs text-rose-500 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="label">Password</label>
            <Link to="/forgot-password" className="text-xs text-brand-600 hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
            <input
              type={showPwd ? 'text' : 'password'}
              autoComplete="current-password"
              className="input pl-9 pr-10"
              placeholder="••••••••"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-2 top-2 btn-ghost p-1"
            >
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-rose-500 mt-1">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          <LogIn size={16} />
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
